import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { uploadAPI } from '../utils/api';
import { toast } from 'sonner';
import { Paperclip, X, Loader2, ZoomIn, Play, FileText, FileIcon, Download } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const MAX_PHOTOS = 3;
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/svg+xml', 'image/webp',
  'video/quicktime', 'video/mp4',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const getFileExtension = (url) => {
  if (!url) return '';
  const clean = url.split('?')[0];
  const ext = clean.split('.').pop().toLowerCase();
  return ext;
};

const getFileType = (url) => {
  const ext = getFileExtension(url);
  if (['jpg', 'jpeg', 'png', 'svg', 'webp'].includes(ext)) return 'image';
  if (['mp4', 'mov'].includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  return 'unknown';
};

const FileThumbnail = ({ url, onClick }) => {
  const type = getFileType(url);
  const ext = getFileExtension(url);

  if (type === 'image') {
    return (
      <img src={url} alt="Archivo" className="w-full h-full object-cover" />
    );
  }

  if (type === 'video') {
    return (
      <div className="w-full h-full bg-gray-900 flex flex-col items-center justify-center gap-1">
        <Play size={24} className="text-white" />
        <span className="text-[10px] text-gray-300 uppercase font-medium">{ext}</span>
      </div>
    );
  }

  if (type === 'pdf') {
    return (
      <div className="w-full h-full bg-red-50 flex flex-col items-center justify-center gap-1">
        <FileText size={24} className="text-red-500" />
        <span className="text-[10px] text-red-500 uppercase font-medium">PDF</span>
      </div>
    );
  }

  if (type === 'docx') {
    return (
      <div className="w-full h-full bg-blue-50 flex flex-col items-center justify-center gap-1">
        <FileIcon size={24} className="text-blue-600" />
        <span className="text-[10px] text-blue-600 uppercase font-medium">DOCX</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
      <FileIcon size={24} className="text-gray-400" />
    </div>
  );
};

const PreviewContent = ({ url }) => {
  const type = getFileType(url);

  if (type === 'image') {
    return (
      <img src={url} alt="Preview" className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
    );
  }

  if (type === 'video') {
    return (
      <video controls autoPlay className="w-full max-h-[80vh] rounded-lg">
        <source src={url} />
        Tu navegador no soporta la reproduccion de video.
      </video>
    );
  }

  if (type === 'pdf') {
    return (
      <iframe src={url} title="PDF Preview" className="w-full h-[80vh] rounded-lg border-0" />
    );
  }

  if (type === 'docx') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <FileIcon size={64} className="text-blue-600" />
        <p className="text-sm text-[#5A5A5A]">La previsualizacion de archivos DOCX no esta disponible</p>
        <a href={url} download target="_blank" rel="noopener noreferrer">
          <Button variant="outline" className="gap-2">
            <Download size={16} />
            Descargar archivo
          </Button>
        </a>
      </div>
    );
  }

  return null;
};

const PhotoUpload = ({ photos = [], onChange, disabled = false, maxPhotos = MAX_PHOTOS }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximo ${maxPhotos} archivos por solicitud`);
      return;
    }

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`Tipo de archivo no permitido: ${file.name}. Use JPG, PNG, SVG, WebP, MOV, MP4, PDF o DOCX`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(`El archivo ${file.name} excede 25MB`);
        continue;
      }

      setUploading(true);
      try {
        const response = await uploadAPI.uploadFoto(file);
        const newPhoto = {
          url: response.data.foto_url,
          filePath: response.data.file_path,
          filename: response.data.filename
        };
        onChange([...photos, newPhoto]);
        toast.success('Archivo subido correctamente');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || 'Error al subir el archivo');
      } finally {
        setUploading(false);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = async (index) => {
    const photo = photos[index];

    try {
      if (photo.filePath) {
        await uploadAPI.deleteFoto(photo.filePath);
      }
      const newPhotos = photos.filter((_, i) => i !== index);
      onChange(newPhotos);
      toast.success('Archivo eliminado');
    } catch (error) {
      console.error('Delete error:', error);
      const newPhotos = photos.filter((_, i) => i !== index);
      onChange(newPhotos);
    }
  };

  return (
    <div className="space-y-2">
      {/* File grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, index) => {
            const url = photo.url || photo;
            return (
              <div
                key={index}
                className="relative aspect-square rounded-lg overflow-hidden border border-[#E8E8E7] group"
                data-testid={`file-thumbnail-${index}`}
              >
                <FileThumbnail url={url} />
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewUrl(url)}
                    className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                    data-testid={`file-preview-btn-${index}`}
                  >
                    <ZoomIn size={16} className="text-gray-700" />
                  </button>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="p-1.5 bg-red-500 rounded-full hover:bg-red-600"
                      data-testid={`file-remove-btn-${index}`}
                    >
                      <X size={16} className="text-white" />
                    </button>
                  )}
                </div>
                {/* Mobile delete button */}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="absolute top-1 right-1 p-1 bg-red-500 rounded-full sm:hidden"
                  >
                    <X size={12} className="text-white" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload button */}
      {!disabled && photos.length < maxPhotos && (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.svg,.webp,.mov,.mp4,.pdf,.docx"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
            data-testid="file-upload-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full border-dashed border-[#c5b8a2] text-[#9d8272] hover:bg-[#9d8272]/10 h-16 sm:h-20"
            data-testid="file-upload-btn"
          >
            {uploading ? (
              <div className="flex items-center gap-2">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-xs sm:text-sm">Subiendo...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Paperclip size={20} className="sm:w-6 sm:h-6" />
                <span className="text-[10px] sm:text-xs">
                  Agregar archivo ({photos.length}/{maxPhotos})
                </span>
              </div>
            )}
          </Button>
        </div>
      )}

      {/* Info text */}
      <p className="text-[10px] sm:text-xs text-[#888888] text-center">
        Max. 25MB por archivo - JPG, PNG, MOV, MP4, PDF, DOCX
      </p>

      {/* Preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-3xl p-2 sm:p-4">
          {previewUrl && <PreviewContent url={previewUrl} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { getFileType, getFileExtension, FileThumbnail, PreviewContent };
export default PhotoUpload;
